import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Truck, Package, XCircle, Route, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import API from '@/lib/api';
import { notificationLink } from '@/lib/notificationLinks';

const NOTIF_ICONS = {
  route_started: { icon: Truck, color: 'text-amber-600', bg: 'bg-amber-50' },
  delivery_completed: { icon: Package, color: 'text-green-600', bg: 'bg-green-50' },
  delivery_failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  route_assigned: { icon: Route, color: 'text-blue-600', bg: 'bg-blue-50' },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!user || !mountedRef.current) return;
    try {
      const res = await API.get('/notifications');
      if (!mountedRef.current) return;
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // Silently fail
    }
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // 30s instead of 12s
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [user, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const markRead = async (id) => {
    try {
      await API.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (err) { console.error('Mark read failed:', err); }
  };

  const markAllRead = async () => {
    try {
      await API.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) { console.error('Mark all read failed:', err); }
  };

  const openNotification = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    navigate(notificationLink(n, user?.role));
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return `il y a ${Math.floor(diff / 86400)}j`;
  };

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost" size="icon"
        className="relative h-9 w-9"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-brick text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl border border-clay shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-clay/50">
            <h3 className="font-serif text-lg">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs font-sans text-brick hover:text-brick/80" onClick={markAllRead} data-testid="mark-all-read">
                <CheckCheck className="h-3 w-3 mr-1" /> Tout marquer lu
              </Button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto overscroll-contain">
            {notifications.length === 0 ? (
              <div className="text-center py-10 px-4" data-testid="no-notifications">
                <Bell className="h-8 w-8 text-clay mx-auto mb-2" />
                <p className="text-sm text-muted-foreground font-sans">Aucune notification</p>
              </div>
            ) : (
              notifications.map(n => {
                const cfg = NOTIF_ICONS[n.type] || { icon: Bell, color: 'text-stone-text', bg: 'bg-clay/20' };
                const Icon = cfg.icon;
                return (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    className={`flex gap-3 px-4 py-3 border-b border-clay/20 cursor-pointer hover:bg-clay/20 active:bg-clay/30 transition-colors ${!n.read ? 'bg-brick/[0.03]' : ''}`}
                    onClick={() => openNotification(n)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openNotification(n); } }}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center mt-0.5`}>
                      <Icon className={`h-4 w-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-sans text-sm ${!n.read ? 'font-semibold text-stone-text' : 'text-muted-foreground'}`}>{n.title}</p>
                        {!n.read && <span className="h-2 w-2 rounded-full bg-brick flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line break-words" data-testid={`notification-message-${n.id}`}>{n.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1 flex items-center gap-1">
                        {timeAgo(n.created_at)} · <span className="text-brick inline-flex items-center gap-0.5">Ouvrir <ArrowUpRight className="h-2.5 w-2.5" /></span>
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
